import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Button,
    FormGroup,
    Label,
    Input,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Spinner,
    Table,
    Pagination,
    PaginationItem,
    PaginationLink,
} from 'reactstrap';
import { useState, useEffect } from 'react';
import { FaPlus, FaEdit } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiURL1 } from '../../config';
import Footer from 'components/Footers/Footer';
import TeacherAttendance from './AdminTeacherAtt';

const StudentAttendance = ({ filterDate, filterName, filterClass, classes, onEditAttendance, refreshAttendance }) => {
    const [attendanceData, setAttendanceData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const token = localStorage.getItem('token');

    useEffect(() => {
        fetchAttendance();
    }, [filterDate, filterName, filterClass, page, refreshAttendance]);

    const fetchAttendance = async () => {
        setLoading(true);
        try {
            const params = { page, limit: 20 };
            if (filterDate) params.date = filterDate;
            if (filterName) params.name = filterName;
            if (filterClass) params.class = filterClass;

            const response = await axios.get(`${apiURL1}/superadmin/getStudentAttendance`, {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });

            // console.log('getStudentAttendance response:', response.data);
            setAttendanceData(response.data.data || []);
            setTotalPages(response.data.totalPages || 1);
        } catch (error) {
            // console.error('Error fetching student attendance:', error.response?.data || error);
            toast.error(error.response?.data?.message || 'Error fetching student attendance!');
        } finally {
            setLoading(false);
        }
    };

    const formatTime = (isoTime) => {
        if (!isoTime) return '-';
        const date = new Date(isoTime);
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    };

    // Generate pagination items
    const getPaginationItems = () => {
        const items = [];
        const maxPagesToShow = 5; // Number of pages to show before/after current page
        const startPage = Math.max(1, page - 2);
        const endPage = Math.min(totalPages, page + 2);

        // Previous button
        items.push(
            <PaginationItem disabled={page === 1} key="prev">
                <PaginationLink previous onClick={() => setPage(page - 1)} />
            </PaginationItem>
        );

        // First page
        if (startPage > 1) {
            items.push(
                <PaginationItem active={page === 1} key={1}>
                    <PaginationLink onClick={() => setPage(1)}>1</PaginationLink>
                </PaginationItem>
            );
        }

        // Ellipsis before
        if (startPage > 2) {
            items.push(
                <PaginationItem disabled key="start-ellipsis">
                    <PaginationLink>...</PaginationLink>
                </PaginationItem>
            );
        }

        // Middle pages
        for (let p = startPage; p <= endPage; p++) {
            items.push(
                <PaginationItem active={page === p} key={p}>
                    <PaginationLink onClick={() => setPage(p)}>{p}</PaginationLink>
                </PaginationItem>
            );
        }

        // Ellipsis after
        if (endPage < totalPages - 1) {
            items.push(
                <PaginationItem disabled key="end-ellipsis">
                    <PaginationLink>...</PaginationLink>
                </PaginationItem>
            );
        }

        // Last page
        if (endPage < totalPages) {
            items.push(
                <PaginationItem active={page === totalPages} key={totalPages}>
                    <PaginationLink onClick={() => setPage(totalPages)}>{totalPages}</PaginationLink>
                </PaginationItem>
            );
        }

        // Next button
        items.push(
            <PaginationItem disabled={page === totalPages} key="next">
                <PaginationLink next onClick={() => setPage(page + 1)} />
            </PaginationItem>
        );

        return items;
    };

    return (
        <>
            {loading ? (
                <Spinner className="my-5 mx-auto" />
            ) : (
                <>
                    <Table responsive>
                        <thead>
                            <tr>
                                <th>Student Name</th>
                                <th>Registration No</th>
                                <th>Class</th>
                                <th>Date</th>
                                <th>Check In</th>
                                <th>Check Out</th>
                                <th>Status</th>
                                <th>Remarks</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {attendanceData.length > 0 ? (
                                attendanceData.map((record) => (
                                    <tr key={`${record.studentId}-${record.date}`}>
                                        <td>{record.fullName || 'Unknown'}</td>
                                        <td>{record.registrationNumber || '-'}</td>
                                        <td>{record.class || 'Unassigned'}</td>
                                        <td>{record.date || '-'}</td>
                                        <td>{formatTime(record.checkIn)}</td>
                                        <td>{formatTime(record.checkOut)}</td>
                                        <td>{record.status || '-'}</td>
                                        <td>{record.remarks || 'None'}</td>
                                        <td>
                                            <Button
                                                color="warning"
                                                size="sm"
                                                onClick={() =>
                                                    onEditAttendance({
                                                        studentId: record.studentId,
                                                        date: record.date,
                                                        status: record.status,
                                                        checkIn: record.checkIn,
                                                        checkOut: record.checkOut,
                                                    })
                                                }
                                                disabled={record.remarks === ''}
                                                title={record.remarks === '' ? 'Cannot edit fingerprint-based records' : ''}
                                            >
                                                <FaEdit /> Edit
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="9" className="text-center">
                                        No attendance records found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                    <Pagination>
                        {getPaginationItems()}
                    </Pagination>
                </>
            )}
        </>
    );
};

const AttendanceOverview = () => {
    const navigate = useNavigate();
    const [view, setView] = useState('teacher');
    const [filterDate, setFilterDate] = useState('');
    const [filterName, setFilterName] = useState('');
    const [filterClass, setFilterClass] = useState('');
    const [classes, setClasses] = useState([]);
    const [addStudentAttendanceModalOpen, setAddStudentAttendanceModalOpen] = useState(false);
    const [newStudentAttendance, setNewStudentAttendance] = useState({
        studentId: '',
        date: '',
        status: 'Present',
    });
    const [isEditing, setIsEditing] = useState(false);
    const [studentNames, setStudentNames] = useState([]);
    const token = localStorage.getItem('token');
    const [loading, setLoading] = useState(false);
    const [refreshAttendance, setRefreshAttendance] = useState(false);

    useEffect(() => {
        fetchStudentNames();
        fetchClasses();
    }, []);

    useEffect(() => {
        if (refreshAttendance) {
            setRefreshAttendance(false);
        }
    }, [refreshAttendance]);

    const fetchStudentNames = async () => {
        try {
            const response = await axios.get(`${apiURL1}/superadmin/getStudentsName`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { limit: 1000 },
            });
            // console.log('getStudentsName response:', response.data);
            setStudentNames(
                response.data.students?.map((student) => ({
                    _id: student._id,
                    fullName: student.fullName || 'Unknown',
                })) || []
            );
            if (!response.data.students?.length) {
                toast.warn('No students found.');
            }
        } catch (error) {
            // console.error('Error fetching student names:', error.response?.data || error);
            toast.error(error.response?.data?.message || 'Error fetching student names!');
            setStudentNames([]);
        }
    };

    const fetchClasses = async () => {
        try {
            const response = await axios.get(`${apiURL1}/superadmin/getClassName`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            // console.log('getClassName response:', response.data);
            setClasses(response.data || []);
        } catch (error) {
            // console.error('Error fetching classes:', error.response?.data || error);
            toast.error(error.response?.data?.message || 'Error fetching classes!');
        }
    };

    const handleViewChange = (newView) => {
        setView(newView);
        setFilterDate('');
        setFilterName('');
        setFilterClass('');
    };

    const handleFilterDateChange = (e) => {
        const date = e.target.value;
        const today = new Date().toISOString().split('T')[0];
        if (date > today) {
            toast.error('Future dates are not allowed.');
            return;
        }
        setFilterDate(date);
    };

    const handleFilterNameChange = (e) => {
        setFilterName(e.target.value);
    };

    const handleClassSelect = (e) => {
        setFilterClass(e.target.value);
    };

    const handleAddStudentAttendanceToggle = (record = null) => {
        if (record) {
            setNewStudentAttendance({
                studentId: record.studentId,
                date: record.date,
                status: record.status,
            });
            setIsEditing(true);
        } else {
            setNewStudentAttendance({ studentId: '', date: '', status: 'Present' });
            setIsEditing(false);
        }
        setAddStudentAttendanceModalOpen(!addStudentAttendanceModalOpen);
    };

    const handleNewStudentAttendanceChange = (e) => {
        setNewStudentAttendance({ ...newStudentAttendance, [e.target.name]: e.target.value });
    };

    const handleAddStudentAttendanceSubmit = async () => {
        setLoading(true);
        try {
            const { studentId, date, status } = newStudentAttendance;

            if (!studentId || !date || !status) {
                toast.error('Please fill all required fields.');
                return;
            }

            const today = new Date().toISOString().split('T')[0];
            if (date > today) {
                toast.error('Future dates are not allowed.');
                return;
            }

            // Check for existing attendance
            const checkResponse = await axios.get(`${apiURL1}/superadmin/getStudentAttendance`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { studentId, date },
            });
            if (checkResponse.data.data?.length > 0 && !isEditing) {
                toast.error('Attendance already exists for this student on this date.');
                return;
            }

            const response = await axios.post(
                `${apiURL1}/superadmin/addStudentAttendance`,
                { studentId, date, status },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            if (response.status === 200) {
                toast.success(`Student attendance ${isEditing ? 'updated' : 'added'} successfully!`);
                setAddStudentAttendanceModalOpen(false);
                setNewStudentAttendance({ studentId: '', date: '', status: 'Present' });
                setIsEditing(false);
                setRefreshAttendance(true);
            } else {
                toast.error(response.data.message || `Failed to ${isEditing ? 'update' : 'add'} student attendance.`);
            }
        } catch (error) {
            console.error('Error adding/updating student attendance:', error.response?.data || error);
            toast.error(error.response?.data?.message || `Error ${isEditing ? 'updating' : 'adding'} student attendance!`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div
                className="header d-flex align-items-center"
                style={{
                    minHeight: '600px',
                    backgroundImage: `url(${require('../../assets/img/theme/alRehman.jpg')})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center top',
                }}
            >
                <span className="mask bg-gradient-default opacity-8" />
                <Container className="d-flex align-items-center" fluid>
                    <Row>
                        <Col lg="7" md="10">
                            <h1 className="display-2 text-white">Attendance Overview</h1>
                            <p className="text-white mt-0 mb-5">
                                View and manage teacher and student attendance records.
                            </p>
                        </Col>
                    </Row>
                </Container>
            </div>
            <Container className="mt--7" fluid>
                <Row>
                    <Col>
                        <Card className="shadow">
                            <CardHeader className="border-0 d-flex justify-content-between align-items-center">
                                <h3 className="mb-0">Attendance Records</h3>
                                <div>
                                    <Button
                                        color={view === 'teacher' ? 'primary' : 'secondary'}
                                        onClick={() => handleViewChange('teacher')}
                                    >
                                        Staff
                                    </Button>
                                    <Button
                                        color={view === 'student' ? 'primary' : 'secondary'}
                                        onClick={() => handleViewChange('student')}
                                    >
                                        Student
                                    </Button>
                                    <Button
                                        color="success"
                                        onClick={() => handleAddStudentAttendanceToggle()}
                                    >
                                        <FaPlus /> Add Attendance
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardBody>
                                <Row className="mb-3">
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>Filter by Date</Label>
                                            <Input
                                                type="date"
                                                value={filterDate}
                                                onChange={handleFilterDateChange}
                                                max={new Date().toISOString().split('T')[0]}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>Filter by Name</Label>
                                            <Input
                                                type="text"
                                                placeholder="Enter student name"
                                                value={filterName}
                                                onChange={handleFilterNameChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                    {view === 'student' && (
                                        <Col md="4">
                                            <FormGroup>
                                                <Label>Filter by Class</Label>
                                                <Input
                                                    type="select"
                                                    value={filterClass}
                                                    onChange={handleClassSelect}
                                                >
                                                    <option value="">All Classes</option>
                                                    {classes.map((cls) => (
                                                        <option key={cls._id} value={cls._id}>
                                                            {cls.className}
                                                        </option>
                                                    ))}
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                    )}
                                </Row>
                                {view === 'teacher' ? (
                                    <TeacherAttendance
                                        filterDate={filterDate}
                                        filterName={filterName}
                                        refreshAttendance={refreshAttendance}
                                        setRefreshAttendance={setRefreshAttendance}
                                    />
                                ) : (
                                    <StudentAttendance
                                        filterDate={filterDate}
                                        filterName={filterName}
                                        filterClass={filterClass}
                                        classes={classes}
                                        onEditAttendance={handleAddStudentAttendanceToggle}
                                        refreshAttendance={refreshAttendance}
                                    />
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <ToastContainer />

            <Modal isOpen={addStudentAttendanceModalOpen} toggle={() => handleAddStudentAttendanceToggle()}>
                <ModalHeader toggle={() => handleAddStudentAttendanceToggle()}>
                    {isEditing ? 'Edit Student Attendance' : 'Add Student Attendance'}
                </ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label>Student Name</Label>
                        <Input
                            type="select"
                            name="studentId"
                            value={newStudentAttendance.studentId}
                            onChange={handleNewStudentAttendanceChange}
                            disabled={isEditing}
                        >
                            <option value="">Select Student</option>
                            {studentNames.map((student) => (
                                <option key={student._id} value={student._id}>
                                    {student.fullName}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>
                    <FormGroup>
                        <Label>Date</Label>
                        <Input
                            type="date"
                            name="date"
                            value={newStudentAttendance.date}
                            onChange={handleNewStudentAttendanceChange}
                            disabled={isEditing}
                            max={new Date().toISOString().split('T')[0]}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label>Status</Label>
                        <Input
                            type="select"
                            name="status"
                            value={newStudentAttendance.status}
                            onChange={handleNewStudentAttendanceChange}
                        >
                            <option value="Present">Present</option>
                            <option value="Absent">Absent</option>
                        </Input>
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="primary"
                        onClick={handleAddStudentAttendanceSubmit}
                        disabled={loading}
                    >
                        {isEditing ? 'Update Attendance' : 'Add Attendance'}
                    </Button>
                    <Button
                        color="secondary"
                        onClick={() => handleAddStudentAttendanceToggle()}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    {loading && <Spinner size="sm" />}
                </ModalFooter>
            </Modal>
            <Footer />
        </>
    );
};

export default AttendanceOverview;